import { Panel, CountKpi } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';
import { count, percent, faintIfNull, NO_DATA } from '../lib/format.js';

interface LandingPage {
  url: string;
  adsPointingHere: number;
  trackingRegistered: boolean;
  visits: number | null;
  submissions: number | null;
  conversionRatePercent: number | null;
}

interface LandingPagesResponse {
  window: { from: string; to: string; days: number };
  pages: LandingPage[];
  trackingHasSeenTraffic: boolean;
  paidClicks: number;
  unrecordedClicks: number;
  pagesNotRegisteredForTracking: number;
  generatedUtc: string;
}

export function LandingPages({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data, error } = useApi<LandingPagesResponse>('/api/landing-pages?days=30', getToken);

  if (error) return <EmptyState title="Could not load landing pages" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading landing pages from the ads that point at them.</EmptyState>;

  return (
    <div className="stack">
      {data.pagesNotRegisteredForTracking > 0 ? (
        <div className="panel" style={{ borderColor: 'var(--negative)' }}>
          <h2>{count(data.pagesNotRegisteredForTracking)} advertised pages are not registered for tracking</h2>
          <p>
            These pages have no row in the landing-page dimension that visit records point at, so a visit
            arriving at one of them <strong>cannot be attributed to it</strong> — the visit would be
            recorded without a page. Their traffic therefore reads {NO_DATA}, not zero. This must be
            resolved before a campaign is enabled, or the advertising spend will produce visits that no
            page can be credited with.
          </p>
        </div>
      ) : null}

      {data.unrecordedClicks > 0 ? (
        <div className="panel" style={{ borderColor: 'var(--negative)' }}>
          <h2>{count(data.unrecordedClicks)} paid clicks arrived and none was recorded</h2>
          <p>
            The campaigns have served and Google has charged for these clicks, but no visit reached
            the attribution store. An enquiry arriving now could not be connected to the advertising
            that produced it. Either the capture script is not running on these pages, or visitors
            are not granting analytics consent — and consent is required, by setting, before anything
            is captured at all.
          </p>
        </div>
      ) : !data.trackingHasSeenTraffic && data.paidClicks === 0 ? (
        <div className="panel" style={{ borderColor: 'var(--warning)' }}>
          <h2>No paid clicks yet</h2>
          <p>
            The pages are published and the ads point at them, but no click has been charged, so
            visits and form submissions read <strong>{NO_DATA}</strong> rather than zero. Once clicks
            arrive, a genuine zero here would mean something quite different — that we measured and
            found no traffic — and the screen will say so.
          </p>
        </div>
      ) : null}

      <Panel
        title="Landing pages"
        subtitle="Derived from what the ads actually point at, not from a list maintained by hand. A page no ad points at is not a landing page for this purpose, and a page the ads do point at cannot be forgotten."
      >
        <div className="kpi-grid">
          <CountKpi label="Pages advertised to" value={data.pages.length} />
          <CountKpi label="Ads pointing at them" value={data.pages.reduce((s, p) => s + p.adsPointingHere, 0)} />
          <CountKpi
            label="Visits"
            value={data.pages.reduce<number | null>((s, p) => (p.visits === null ? s : (s ?? 0) + p.visits), null)}
            note={`Window ${data.window.from} to ${data.window.to}`}
          />
          <CountKpi
            label="Form submissions"
            value={data.pages.reduce<number | null>((s, p) => (p.submissions === null ? s : (s ?? 0) + p.submissions), null)}
            note="A submission is not yet an opportunity"
          />
        </div>
      </Panel>

      <Panel title="By page" subtitle={`Window: ${data.window.from} to ${data.window.to}.`}>
        <table className="data">
          <thead>
            <tr>
              <th>Page</th>
              <th className="num">Ads pointing here</th>
              <th>Tracking</th>
              <th className="num">Visits</th>
              <th className="num">Submissions</th>
              <th className="num">Conversion rate</th>
            </tr>
          </thead>
          <tbody>
            {data.pages.map((p) => (
              <tr key={p.url}>
                <td>
                  <a href={p.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>
                    {p.url.replace(/^https?:\/\//, '')}
                  </a>
                </td>
                <td className="num">{count(p.adsPointingHere)}</td>
                <td>
                  {p.trackingRegistered
                    ? <span className="pill good">Registered</span>
                    : <span className="pill bad">Not registered</span>}
                </td>
                <td className="num" style={faintIfNull(p.visits)}>{count(p.visits)}</td>
                <td className="num" style={faintIfNull(p.submissions)}>{count(p.submissions)}</td>
                <td className="num" style={faintIfNull(p.conversionRatePercent)}>{percent(p.conversionRatePercent)}</td>
              </tr>
            ))}
            {data.pages.length === 0 ? (
              <tr><td colSpan={6} style={{ color: 'var(--text-faint)' }}>No ad currently points at a landing page.</td></tr>
            ) : null}
          </tbody>
        </table>
        <p style={{ marginTop: 14, fontSize: 12, color: 'var(--text-faint)' }}>
          Conversion rate is submissions ÷ visits. It stays {NO_DATA} while visits are zero — dividing by
          an unmeasured denominator would produce a number that looks like performance and is not. A page
          that is not registered for tracking shows {NO_DATA} regardless of what the campaigns do.
        </p>
      </Panel>
    </div>
  );
}
